import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, Activity } from "lucide-react";

export default function MarketIntel() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [filter, setFilter] = useState({ intel_type: "", competitor_brand: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    competitor_brand: "", competitor_product: "", intel_type: "price",
    pharmacy_id: "", doctor_id: "", description: "", price: "", severity: "medium",
  });

  const load = async () => {
    const params = {};
    if (filter.intel_type) params.intel_type = filter.intel_type;
    if (filter.competitor_brand) params.competitor_brand = filter.competitor_brand;
    const [m, p, d] = await Promise.all([api.get("/market-intel", { params }), api.get("/pharmacies"), api.get("/doctors")]);
    setList(m.data); setPharmacies(p.data); setDoctors(d.data);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.price === "") payload.price = null;
    else payload.price = Number(payload.price);
    if (!payload.pharmacy_id) payload.pharmacy_id = null;
    if (!payload.doctor_id) payload.doctor_id = null;
    await api.post("/market-intel", payload);
    setOpen(false);
    setForm({ competitor_brand: "", competitor_product: "", intel_type: "price", pharmacy_id: "", doctor_id: "", description: "", price: "", severity: "medium" });
    await load();
  };

  const sevColor = (s) => s === "high" ? "red" : s === "medium" ? "amber" : "slate";
  const typeColor = (t) => t === "price" ? "blue" : t === "offer" ? "purple" : t === "activity" ? "amber" : t === "shortage" ? "red" : t === "feedback" ? "slate" : "green";

  return (
    <div data-testid="market-intel-page">
      <PageHeader
        title={t("marketIntel")}
        subtitle="Competitor tracking & market opportunities"
        actions={<Btn onClick={() => setOpen(true)} data-testid="add-intel-btn"><Plus className="w-4 h-4"/>Log Intel</Btn>}
      />

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select value={filter.intel_type} onChange={(e) => setFilter({...filter, intel_type: e.target.value})}>
            <option value="">All types</option>
            <option value="price">Price</option><option value="offer">Offer</option><option value="activity">Activity</option>
            <option value="shortage">Shortage</option><option value="feedback">Feedback</option><option value="trend">Trend</option>
          </Select>
          <Input placeholder="Competitor brand..." value={filter.competitor_brand} onChange={(e) => setFilter({...filter, competitor_brand: e.target.value})}/>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">Date</th>
                <th className="py-2 px-3 text-start">Type</th>
                <th className="py-2 px-3 text-start">Brand</th>
                <th className="py-2 px-3 text-start">Product</th>
                <th className="py-2 px-3 text-end">Price</th>
                <th className="py-2 px-3 text-start">Severity</th>
                <th className="py-2 px-3 text-start">Description</th>
                <th className="py-2 px-3 text-start">Logged By</th>
              </tr>
            </thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 text-xs">{fmtDate(m.created_at)}</td>
                  <td className="py-2 px-3"><Badge color={typeColor(m.intel_type)}>{m.intel_type}</Badge></td>
                  <td className="py-2 px-3 font-semibold">{m.competitor_brand}</td>
                  <td className="py-2 px-3 text-slate-700">{m.competitor_product || "-"}</td>
                  <td className="py-2 px-3 text-end font-mono">{m.price ? `${m.price} EGP` : "-"}</td>
                  <td className="py-2 px-3"><Badge color={sevColor(m.severity)}>{m.severity}</Badge></td>
                  <td className="py-2 px-3 max-w-md text-slate-600 text-xs">{m.description}</td>
                  <td className="py-2 px-3 text-xs">{m.rep_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Log Market Intelligence">
        <form onSubmit={submit} className="space-y-3" data-testid="intel-form">
          <Input label="Competitor Brand *" required value={form.competitor_brand} onChange={(e) => setForm({...form, competitor_brand: e.target.value})}/>
          <Input label="Product" value={form.competitor_product} onChange={(e) => setForm({...form, competitor_product: e.target.value})}/>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" value={form.intel_type} onChange={(e) => setForm({...form, intel_type: e.target.value})}>
              <option value="price">Price</option><option value="offer">Offer</option><option value="activity">Activity</option>
              <option value="shortage">Shortage</option><option value="feedback">Feedback</option><option value="trend">Trend</option>
            </Select>
            <Select label="Severity" value={form.severity} onChange={(e) => setForm({...form, severity: e.target.value})}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </Select>
            <Input label="Price" type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})}/>
            <Select label="At Pharmacy" value={form.pharmacy_id} onChange={(e) => setForm({...form, pharmacy_id: e.target.value})}>
              <option value="">--</option>{pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <Select label="From Doctor" value={form.doctor_id} onChange={(e) => setForm({...form, doctor_id: e.target.value})}>
              <option value="">--</option>{doctors.map(d => <option key={d.id} value={d.id}>{d.full_name}</option>)}
            </Select>
          </div>
          <Textarea label="Description *" rows={3} required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit">{t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
